import { NavLink } from 'react-router';
import { useQueuedCount } from '@/features/chats/api';
import { useCan } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { NAV_SECTIONS, type NavItem } from '../nav';

function QueuedBadge() {
  const can = useCan();
  const { data } = useQueuedCount(can('chats:handle'));
  if (!data?.count) return null;
  return (
    <span
      className="ml-auto min-w-5 rounded-full bg-destructive px-1.5 text-center text-2xs leading-5 font-semibold text-destructive-foreground"
      aria-label={`${data.count} queued chats`}
    >
      {data.more ? '99+' : data.count}
    </span>
  );
}

function NavEntry({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex h-8 items-center gap-2.5 rounded-md px-2.5 text-ui font-medium text-sidebar-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
          isActive && 'bg-primary/10 text-primary hover:bg-primary/12 hover:text-primary',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
      {item.badge === 'queuedChats' && <QueuedBadge />}
    </NavLink>
  );
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const can = useCan();
  return (
    <nav aria-label="Main" className="flex flex-col gap-4 px-2 py-3">
      {NAV_SECTIONS.map((section, i) => {
        const items = section.items.filter((item) => can(item.permission));
        if (!items.length) return null;
        return (
          <div key={section.title ?? i} className="grid gap-0.5">
            {section.title && (
              <p className="px-2.5 pb-1 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                {section.title}
              </p>
            )}
            {items.map((item) => (
              <NavEntry key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}

export function Brand() {
  return (
    <div className="flex h-12 items-center gap-2 border-b px-4">
      <img src="/favicon.svg" alt="" className="size-6 shrink-0" />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-ui font-semibold">VMC Citizen Service</p>
        <p className="truncate text-2xs text-muted-foreground">Vadodara Municipal Corporation</p>
      </div>
    </div>
  );
}
